import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { Alexandra } from "@/components/home/Alexandra";
import { FabricStrip } from "@/components/home/FabricStrip";
import { FinalCta } from "@/components/home/FinalCta";
import { PageHero } from "@/components/brand/PageHero";
import { FaqList, Process } from "@/components/home/Sections";
import { LEGAL_PAGES, SITE, whatsappUrl } from "@/config/site";
import { usePageMeta } from "@/lib/seo";
import { PAGE_VICHY_FIT, pageVichy } from "@/lib/patternBg";
import { TERMOS } from "@/config/termos";
import { PRIVACIDADE } from "@/config/privacidade";

// Cabeçalho de página; com `image`, a fotografia fica de fundo (com um véu claro à esquerda para o texto ler bem).
const PageHead = ({ eyebrow, title, intro, image, imagePos = "50% 50%" }) => {
  const body = (
    <div className={`bb-container ${image ? "py-14 sm:py-20 lg:py-24" : "pb-12 pt-12 sm:pt-16"}`}>
      <p className="bb-eyebrow mb-4">{eyebrow}</p>
      <h1 className="max-w-4xl font-serif text-[2.6rem] font-normal leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl">{title}</h1>
      {intro && <p className="mt-6 max-w-2xl text-base leading-relaxed text-taupe md:text-lg">{intro}</p>}
    </div>
  );
  if (!image) return body;
  return (
    <header className="relative isolate overflow-hidden">
      <img src={`/img/catalog/${image}-1200.webp`} srcSet={`/img/catalog/${image}-400.webp 400w, /img/catalog/${image}-1200.webp 1200w`} sizes="100vw" alt="" aria-hidden="true" className="absolute inset-0 -z-10 h-full w-full object-cover" style={{ objectPosition: imagePos }} />
      <span aria-hidden="true" className="absolute inset-0 -z-10 bg-[linear-gradient(90deg,rgba(250,245,241,.94)_0%,rgba(250,245,241,.82)_45%,rgba(250,245,241,.45)_100%)]" />
      {body}
    </header>
  );
};

export function AboutPage() {
  usePageMeta("Sobre a Babybib", "A Babybib é uma marca portuguesa de costura criativa para bebés, com peças feitas à mão pela Alexandra.");
  return (
    <div data-testid="about-page">
      <PageHero image="cj1" pos="50% 45%" title={<>Costura criativa <em className="bb-accent text-[#F6CFCB]">para bebés</em></>} intro="Peças únicas feitas à mão por mim." />
      <Alexandra withLink={false} />
      <FabricStrip />
      <Process last={false} />
      <FinalCta up />
    </div>
  );
}

export function FaqPage() {
  usePageMeta("Perguntas frequentes", "Respostas sobre peças, personalização, prazos e envios da Babybib.");
  return (
    <div data-testid="faq-page">
      <PageHead eyebrow="Perguntas frequentes" title="Tudo o que precisas de saber" intro="Algumas respostas dependem de informação que ainda vou confirmar." />
      <div className="bb-container max-w-4xl"><FaqList /></div>
    </div>
  );
}

const Item = ({ icon: Icon, label, children }) => (
  <div className="flex gap-4 border-b border-ink/10 py-6">
    <Icon className="mt-1 h-5 w-5 shrink-0 text-bordeaux" />
    <div><p className="text-xs uppercase tracking-[0.18em] text-taupe">{label}</p><div className="mt-1 font-serif text-2xl">{children}</div></div>
  </div>
);

export function ContactPage() {
  usePageMeta("Contactos", "Fala com a Babybib.");
  return (
    <div data-testid="contact-page">
      <PageHead eyebrow="Contactos" title="Fala comigo" intro="Para dúvidas sobre peças, padrões ou personalização. Se preferires, também podes fazer a tua encomenda por mensagem: eu respondo-te diretamente." />
      <div className="bb-container max-w-3xl">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-ink/10 pb-6" data-testid="contact-ways">
          <Link to="/loja" data-testid="contact-shop-link" className="bb-btn-primary">Comprar online</Link>
          <p className="text-sm text-taupe-strong">ou fala diretamente comigo, por mensagem.</p>
        </div>
        {whatsappUrl() && (
          <Item icon={MessageCircle} label="WhatsApp">
            <a href={whatsappUrl("Olá Alexandra! Gostava de fazer uma encomenda 😊")} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="bb-link bb-tap">Enviar mensagem</a>
          </Item>
        )}
        <Item icon={Instagram} label="Instagram">
          <a href={SITE.instagram.url} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="bb-link bb-tap">{SITE.instagram.handle}</a>
        </Item>
        <Item icon={Mail} label="Email"><span data-testid="contact-email" className="text-taupe">{SITE.contact.email}</span></Item>
        <Item icon={Phone} label="Telefone"><span className="text-taupe">{SITE.contact.phone}</span></Item>
        <Item icon={MapPin} label="Origem">{SITE.contact.location}</Item>
      </div>
    </div>
  );
}

// Texto com **negrito** e [ligações](/caminho).
const RichText = ({ text }) =>
  text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/).map((part, i) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i} className="font-medium text-ink">{bold[1]}</strong>;
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) return <Link key={i} to={link[2]} className="bb-link text-bordeaux">{link[1]}</Link>;
    return part;
  });

const LEGAL_DOCS = { termos: TERMOS, privacidade: PRIVACIDADE };

function LegalBody({ doc, slug }) {
  const others = LEGAL_PAGES.filter((p) => p.slug !== slug);
  return (
    <div className="bb-container grid gap-10 lg:grid-cols-12 lg:gap-14">
      {/* Índice: em ecrã largo fica fixo ao lado; no telemóvel aparece antes do texto. */}
      <nav aria-label="Índice" className="lg:col-span-4 lg:self-start lg:sticky lg:top-24" data-testid="legal-toc">
        <div className="rounded-3xl bg-linen p-5 sm:p-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-bordeaux">Neste documento</p>
          <ol className="mt-3 space-y-0.5 text-sm">
            {doc.sections.map((sec, i) => (
              <li key={sec.title}>
                <a href={`#seccao-${i + 1}`} className="bb-tap flex gap-2.5 rounded-lg py-1.5 text-ink/80 transition-colors hover:text-bordeaux">
                  <span className="w-5 shrink-0 text-taupe">{i + 1}.</span>
                  <span>{sec.title}</span>
                </a>
              </li>
            ))}
          </ol>
        </div>
      </nav>

      <div className="lg:col-span-8">
        {doc.updated && <p className="inline-flex rounded-full bg-rose-soft px-3 py-1 text-xs text-bordeaux">Última atualização: {doc.updated}</p>}
        {doc.intro && (
          <div className="mt-5 space-y-3 border-l-2 border-bordeaux/30 pl-5 font-serif text-lg leading-relaxed text-ink/85">
            {doc.intro.map((t) => <p key={t}>{t}</p>)}
          </div>
        )}
        <div className="mt-10 space-y-6">
          {doc.sections.map((sec, i) => (
            <section key={sec.title} id={`seccao-${i + 1}`} data-testid={`legal-section-${i + 1}`} className="scroll-mt-24 rounded-3xl border border-ink/10 bg-white/50 p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-soft font-serif text-lg text-bordeaux">{i + 1}</span>
                <h2 className="pt-0.5 font-serif text-2xl leading-snug text-ink">{sec.title}</h2>
              </div>
              <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-taupe-strong sm:pl-[3.25rem]">
                {sec.body.map((b, j) =>
                  typeof b === "string" ? (
                    <p key={j}><RichText text={b} /></p>
                  ) : (
                    <ul key={j} className="space-y-2">
                      {b.list.map((li) => (
                        <li key={li} className="flex gap-3"><span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-bordeaux/60" /><span>{li}</span></li>
                      ))}
                    </ul>
                  ),
                )}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-10 rounded-3xl bg-linen p-6 sm:p-8">
          <p className="font-serif text-2xl text-ink">Ficou com alguma dúvida?</p>
          <p className="mt-2 text-sm leading-relaxed text-taupe-strong">Respondo-te diretamente.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/contactos" className="bb-btn-primary">Falar comigo</Link>
            {others.map((p) => <Link key={p.slug} to={`/${p.slug}`} className="bb-btn-ghost">{p.title}</Link>)}
          </div>
        </div>
      </div>
    </div>
  );
}

export function LegalPage({ slug }) {
  const page = LEGAL_PAGES.find((p) => p.slug === slug);
  usePageMeta(page?.title, `${page?.title} da loja Babybib.`);
  if (LEGAL_DOCS[slug]) {
    return (
      <div data-testid={`legal-page-${slug}`} className={`${PAGE_VICHY_FIT} pb-[calc(9rem+30px)]`} style={pageVichy()}>
        <PageHead eyebrow="Informação" title={page.title} />
        <LegalBody doc={LEGAL_DOCS[slug]} slug={slug} />
      </div>
    );
  }
  return (
    <div data-testid={`legal-page-${slug}`}>
      <PageHead eyebrow="Informação" title={page?.title} />
      <div className="bb-container max-w-3xl">
        <div className="rounded-3xl border border-dashed border-bordeaux/40 bg-rose-soft/60 p-8">
          <p className="font-serif text-2xl">Texto a fornecer ou validar pela Babybib</p>
          <p className="mt-3 text-sm leading-relaxed text-taupe-strong">Esta página está preparada para receber o texto definitivo. Não é apresentado conteúdo provisório como se fosse oficial.</p>
          <Link to="/contactos" className="bb-link bb-tap mt-3 text-sm font-medium text-bordeaux">Contactos</Link>
        </div>
      </div>
    </div>
  );
}
